/**
 * TripCard component - Displays a trip summary with image, status, name, and dates
 * Matches the design from TripListDashboard.html
 */

import { Pencil, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/dates';
import type { Trip } from '../../types/trip';

interface TripCardProps {
  trip: Trip;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

// Helper function to get trip status based on dates
function getTripStatus(trip: Trip): { label: string; color: string; dotColor: string } {
  const now = new Date();
  const start = new Date(trip.startDate);
  const end = new Date(trip.endDate);

  if (now < start) {
    return {
      label: '規劃中',
      color: 'bg-blue-50 text-blue-600',
      dotColor: 'bg-blue-500'
    };
  } else if (now >= start && now <= end) {
    return {
      label: '進行中',
      color: 'bg-green-50 text-green-600',
      dotColor: 'bg-green-500'
    };
  } else {
    return {
      label: '已結束',
      color: 'bg-gray-50 text-gray-600',
      dotColor: 'bg-gray-500'
    };
  }
}

// Helper function to get placeholder image URL
function getPlaceholderImage(): string {
  // Using default image for all trips
  return 'https://lh3.googleusercontent.com/aida-public/AB6AXuCjCPi9YqSjfO9jjVb4u0MI92iphuj9f44snVt5T5FEYwIK2HS4XdRj2OJGcwTDz6XoJCJwXK5XVWKku-eBP2Ok6V4FjK6ZzbMMc9ZL0JK2L2mfv34hleEYcOeuB8lKA5_fCfpD_wnfc2TcL_iNvgC0okiWORDMhviFITqTcfsy033KIpA_dk0TcoKx8nHarPNNQbbJ_mhQInKNOqAMIuQLpM5Dd1BKqLGeSHYGXFl0FDIAWKjzqiVvABbGxtU7pAfg-gMkMysqG-Wc';
}

export default function TripCard({ trip, onClick, onEdit, onDelete }: TripCardProps) {
  const status = getTripStatus(trip);
  const imageUrl = getPlaceholderImage();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className="group relative flex flex-col rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-transparent hover:border-primary/20 overflow-hidden"
      onClick={handleClick}
    >
      {/* Trip image - top on desktop, bottom on mobile */}
      <div
        className="order-2 md:order-1 mx-4 mb-0 mt-0 md:mx-0 md:mt-0 h-40 bg-center bg-no-repeat bg-cover relative md:rounded-none rounded-lg"
        style={{ backgroundImage: `url("${imageUrl}")` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
      </div>

      {/* Trip info section */}
      <div className="order-1 md:order-2 flex flex-col gap-1 p-4">
        {/* Status badge + edit/delete actions */}
        <div className="flex items-center justify-between gap-2">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${status.color} w-fit`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dotColor}`}></span>
            <span className="text-xs font-semibold uppercase tracking-wide">{status.label}</span>
          </div>
          {(onEdit || onDelete) && (
            <div
              className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-primary transition-colors"
                >
                  <Pencil size={15} />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Trip name */}
        <h3 className="text-[#111718] text-lg font-bold leading-tight mt-1">
          {trip.name}
        </h3>

        {/* Date range */}
        <div className="flex items-center gap-1.5 text-gray-500 text-sm font-medium">
          <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>{formatDate(trip.startDate)} - {formatDate(trip.endDate)}</p>
        </div>
      </div>
    </div>
  );
}
